import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hydrateActivities } from "../contexts/ActivityContext";
import { crashlytics } from "../services/firebase";
import {
  getUserDeclinedActivities,
  getUserCreatedActivities,
  getUserJoinedActivities,
  getUserExpiredActivities,
  getUserDeletedActivities,
  type ActivityPayload,
} from "../services/functionsApi";
import { toJsDate } from "../utils/toJsDate";
import type { ActivityRequest, User } from "../types";

const normalize = (a: ActivityRequest | ActivityPayload): ActivityRequest => ({
  ...a,
  participantIds:
    (a as any).participantIds ||
    (a as any).participants?.map((p: any) => p?.id).filter(Boolean) ||
    [],
  currentSize: (a as any).currentSize || (a as any).participantIds?.length || 0,
  creatorId:
    (a as any).creatorId || (a as any).creator?.id || (a as any).creator?.uid || "",
} as ActivityRequest);

type Loaders = {
  created: boolean;
  joined: boolean;
  expired: boolean;
  declined: boolean;
  deleted: boolean;
};

type Loaded = {
  created: boolean;
  joined: boolean;
  expired: boolean;
  declined: boolean;
  deleted: boolean;
};

type UseProfileActivitiesArgs = {
  displayedUser: User | null;
  currentUser: User | null;
  getUserById?: (id: string) => Promise<User | null | undefined>;
};

type UseProfileActivitiesResult = {
  created: ActivityRequest[];
  joined: ActivityRequest[];
  expired: ActivityRequest[];
  declined: ActivityRequest[];
  deleted: ActivityRequest[];
  loading: Loaders;
  loaded: Loaded;
  loadCreatedTab: () => Promise<void>;
  loadJoinedTab: () => Promise<void>;
  loadExpiredTab: () => Promise<void>;
  loadDeclinedTab: () => Promise<void>;
  loadDeletedTab: () => Promise<void>;
};

export type TabId =
  | "created"
  | "joined"
  | "expired"
  | "declined"
  | "deleted"
  | "audit";

export function useProfileActivities({
  displayedUser,
  currentUser,
  getUserById,
}: UseProfileActivitiesArgs): UseProfileActivitiesResult {
  const [created, setCreated] = useState<ActivityRequest[]>([]);
  const [joined, setJoined] = useState<ActivityRequest[]>([]);
  const [expired, setExpired] = useState<ActivityRequest[]>([]);
  const [declined, setDeclined] = useState<ActivityRequest[]>([]);
  const [deletedRaw, setDeleted] = useState<ActivityRequest[]>([]);
  const [loading, setLoading] = useState<Loaders>(
    {
      created: false,
      joined: false,
      expired: false,
      declined: false,
      deleted: false,
    },
  );
  const [loaded, setLoaded] = useState<Loaded>(
    {
      created: false,
      joined: false,
      expired: false,
      declined: false,
      deleted: false,
    },
  );

  useEffect(() => {
    setCreated([]);
    setJoined([]);
    setExpired([]);
    setDeclined([]);
    setDeleted([]);
    setLoaded({
      created: false,
      joined: false,
      expired: false,
      declined: false,
      deleted: false,
    });
  }, [displayedUser?.id]);

  const hydrateWithUsers = useCallback(
    async (raw: Array<ActivityRequest | ActivityPayload>) =>
      hydrateActivities(
        raw.map((a) => normalize(a)),
        getUserById
          ? async (ids: string[]) => {
              try {
                const users = await Promise.all(
                  ids.map((id) => getUserById(id).catch(() => undefined)),
                );
                return users.filter((u): u is User => !!u);
              } catch {
                return [];
              }
            }
          : async () => [],
      ),
    [getUserById],
  );

  const loadCreatedTab = useCallback(async () => {
    if (!displayedUser || loaded.created) return;
    setLoading((l) => ({ ...l, created: true }));
    try {
      const response = await getUserCreatedActivities({
        userId: displayedUser.id,
      });
      const hydrated = await hydrateWithUsers(response.activities as any);
      setCreated(hydrated);
      setLoaded((prev) => ({ ...prev, created: true }));
    } catch (error) {
      crashlytics.logError(
        "loadCreated failed",
        "useProfileActivities",
        error as any,
      );
      setCreated([]);
      setLoaded((prev) => ({ ...prev, created: true }));
    } finally {
      setLoading((l) => ({ ...l, created: false }));
    }
  }, [displayedUser, hydrateWithUsers, loaded.created]);

  const loadJoinedTab = useCallback(async () => {
    if (!displayedUser || loaded.joined) return;
    setLoading((l) => ({ ...l, joined: true }));
    try {
      const response = await getUserJoinedActivities({
        userId: displayedUser.id,
      });
      const hydrated = await hydrateWithUsers(response.activities as any);
      setJoined(hydrated);
      setLoaded((prev) => ({ ...prev, joined: true }));
    } catch (error) {
      crashlytics.logError(
        "loadJoined failed",
        "useProfileActivities",
        error as any,
      );
      setJoined([]);
      setLoaded((prev) => ({ ...prev, joined: true }));
    } finally {
      setLoading((l) => ({ ...l, joined: false }));
    }
  }, [displayedUser, hydrateWithUsers, loaded.joined]);

  const loadExpiredTab = useCallback(async () => {
    if (!displayedUser || loaded.expired) return;
    setLoading((l) => ({ ...l, expired: true }));
    try {
      const response = await getUserExpiredActivities({
        userId: displayedUser.id,
      });
      const hydrated = await hydrateWithUsers(response.activities as any);
      setExpired(hydrated);
      setLoaded((prev) => ({ ...prev, expired: true }));
    } catch (error) {
      crashlytics.logError(
        "loadExpired failed",
        "useProfileActivities",
        error as any,
      );
      setExpired([]);
      setLoaded((prev) => ({ ...prev, expired: true }));
    } finally {
      setLoading((l) => ({ ...l, expired: false }));
    }
  }, [displayedUser, hydrateWithUsers, loaded.expired]);

  const loadDeclinedTab = useCallback(async () => {
    if (!displayedUser || loaded.declined) return;
    if (!currentUser || currentUser.id !== displayedUser.id) {
      setDeclined([]);
      setLoaded((prev) => ({ ...prev, declined: true }));
      return;
    }
    setLoading((l) => ({ ...l, declined: true }));
    try {
      const response = await getUserDeclinedActivities({ userId: displayedUser.id });
      if (response?.success) {
        const hydrated = await hydrateWithUsers(response.declined || []);
        setDeclined(hydrated);
      } else {
        setDeclined([]);
      }
      setLoaded((prev) => ({ ...prev, declined: true }));
    } catch (error) {
      crashlytics.logError(
        "loadDeclined failed",
        "useProfileActivities",
        error as any,
      );
      setDeclined([]);
      setLoaded((prev) => ({ ...prev, declined: true }));
    } finally {
      setLoading((l) => ({ ...l, declined: false }));
    }
  }, [displayedUser, currentUser, hydrateWithUsers, loaded.declined]);

  const loadDeletedTab = useCallback(async () => {
    if (!displayedUser || loaded.deleted) return;
    setLoading((l) => ({ ...l, deleted: true }));
    try {
      const response = await getUserDeletedActivities({ userId: displayedUser.id });
      const hydrated = await hydrateWithUsers(response.activities as any);
      setDeleted(hydrated);
      setLoaded((prev) => ({ ...prev, deleted: true }));
    } catch (error) {
      crashlytics.logError(
        "loadDeleted failed",
        "useProfileActivities",
        error as any,
      );
      setDeleted([]);
      setLoaded((prev) => ({ ...prev, deleted: true }));
    } finally {
      setLoading((l) => ({ ...l, deleted: false }));
    }
  }, [displayedUser, hydrateWithUsers, loaded.deleted]);

  const deleted = useMemo(
    () =>
      deletedRaw.slice().sort((a, b) => {
        const aDate = toJsDate(a.deletedAt || a.dateTime).getTime();
        const bDate = toJsDate(b.deletedAt || b.dateTime).getTime();
        return bDate - aDate;
      }),
    [deletedRaw],
  );

  return {
    created,
    joined,
    expired,
    declined,
    deleted,
    loading,
    loaded,
    loadCreatedTab,
    loadJoinedTab,
    loadExpiredTab,
    loadDeclinedTab,
    loadDeletedTab,
  };
}
